import React, { createContext, useContext, useEffect, useState } from "react";
import { Appearance, Platform } from "react-native";

type ThemeType = "light" | "dark";

interface ThemeContextProps {
  theme: ThemeType;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps>({
  theme: "light",
  toggleTheme: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const colorScheme = Appearance.getColorScheme();
  const [theme, setTheme] = useState<ThemeType>(
    (colorScheme as ThemeType) || "light",
  );

  // Listen to system color scheme changes
  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setTheme((colorScheme as ThemeType) || "light");
    });
    return () => subscription.remove();
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Web: Ensure body class is set for dark/light backgrounds with smooth transitions
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.body.classList.remove("dark-mode", "light-mode");
      document.body.classList.add(
        theme === "dark" ? "dark-mode" : "light-mode",
      );
    }
  }, [theme]);

  // On web: set body class on initial mount (covers SSR/hydration edge case)
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.body.classList.remove("dark-mode", "light-mode");
      document.body.classList.add(
        theme === "dark" ? "dark-mode" : "light-mode",
      );
    }
    // No deps: only runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }} data-oid="b9as3pb">
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
